import React, { useState, useImperativeHandle, forwardRef, useMemo, useEffect } from "react";
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Slider,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import { useGetJeopardyStakeholders } from "../../../shared/hooks";
import { AutoComplete } from "../../../shared/components";
import { useGetSchedules } from "../mutations";
import { getActiveOutageWindows } from "../utils/outageWindowUtils";
import { formatDate } from "../../../shared/utils";
import { useCapabilities } from "../../../hooks/useCapabilities";

export interface PhaseFormHandle {
  submit: () => void;
};

interface OutageWindowValidationError {
  open: boolean;
  outageWindowEndDate: string | null;
  activeWindowsCount?: number;
}

interface CompletionValidationError {
  open: boolean;
  message: string;
  canProceed: boolean; 
}

interface PhaseFormProps {
  onSave: (data: PhaseFormData) => void;
  onCancel: () => void;
  initialData?: Partial<PhaseFormData>;
  isEditing?: boolean;
  projectId: string | undefined;
  phaseDisabledConfig: Record<string, boolean>;
  isStageGateCompleted: boolean;
  areAllActivitiesCompleted?: boolean;
  hasLinkedStageGate?: boolean;
  isLinkedStageGateCompleted?: boolean;
  outageWindows?: Array<{ id: number; outage_start_date: string; outage_end_date: string; status?: string }>;
};

export interface PhaseFormData {
  phaseId?: number;
  phase: string;
  owner: string;
  userId?: number;
  plannedStartDate: string | null;
  plannedEndDate: string | null;
  status: string;
  color: string;
  progress: number;
  actualEndDate: string | null;
};

const STATUS_OPTIONS = ["Not Started", "In Progress", "Completed", "On Hold"];

export const PhaseForm = forwardRef<PhaseFormHandle, PhaseFormProps>(
  function PhaseForm({ onSave, onCancel, initialData, isEditing, projectId, phaseDisabledConfig, isStageGateCompleted, areAllActivitiesCompleted, hasLinkedStageGate, isLinkedStageGateCompleted, outageWindows }, ref) {

    const { data: stakeholders, isLoading: isLoadingUsers } = useGetJeopardyStakeholders({ params: { project_id: projectId } });
    const { data: phaseData } = useGetSchedules(Number(projectId));
    const { isAllowedToOverrideCommissionPhaseCompletion } = useCapabilities();
    const userOptions = useMemo(() => {
      return stakeholders?.data?.flatMap((role: any) =>
        role?.users?.map((user: any) => ({
          role: role.role_name,
          name: user.user_name,
          phone: user.phone || "N/A",
          email: user.email,
          id: user.user_id,
        }))
      ) || [];
    }, [stakeholders, isLoadingUsers]);

    const defaultFormData: PhaseFormData = {
      phaseId: undefined,
      phase: "",
      owner: "",
      userId: undefined,
      plannedStartDate: null,
      plannedEndDate: null,
      status: "Not Started",
      color: "#2196F3",
      progress: 0,
      actualEndDate: null,
    };


    const [formData, setFormData] = useState<PhaseFormData>({
      phaseId: initialData?.phaseId,
      phase: initialData?.phase || "",
      owner: initialData?.owner || "",
      userId: initialData?.userId,
      plannedStartDate: initialData?.plannedStartDate || null,
      plannedEndDate: initialData?.plannedEndDate || null,
      status: initialData?.status || "Not Started",
      color: initialData?.color || "#2196F3",
      progress: initialData?.progress || 0,
      actualEndDate: initialData?.actualEndDate || null,
    });

    const [errors, setErrors] = useState<{
      phase?: string;
      plannedStartDate?: string;
      plannedEndDate?: string;
    }>({});

    const [warningQueue, setWarningQueue] = useState<string[]>([]);

    const [outageWindowValidationError, setOutageWindowValidationError] = useState<OutageWindowValidationError>({
      open: false,
      outageWindowEndDate: null,
      activeWindowsCount: 0,
    });

    const [completionValidationError, setCompletionValidationError] = useState<CompletionValidationError>({
      open: false,
      message: "",
      canProceed: false,
    });

    const currentWarning = warningQueue[0] ?? null;

    const handleWarningIgnore = () => {
      const remaining = warningQueue.slice(1);
      setWarningQueue(remaining);
      if (remaining.length === 0) {
        onSave(formData);
      }
    };

    const handleWarningCancel = () => {
      setWarningQueue([]);
    };

    const handleOutageWindowValidationClose = () => {
      setOutageWindowValidationError({ open: false, outageWindowEndDate: null, activeWindowsCount: 0 });
    };

    const handleCompletionValidationClose = () => {
      setCompletionValidationError({ open: false, message: "", canProceed: false });
    };

    const handleCompletionValidationProceed = () => {
      setCompletionValidationError({ open: false, message: "", canProceed: false });
      onSave(formData);
    };

    const isCommissioningPhase = formData.phase.toLowerCase().includes("commissioning");

    const validateCommissioningPhaseAgainstOutageWindows = (): boolean => {
      if (!isCommissioningPhase || !outageWindows || outageWindows.length === 0) {
        return true;
      }

      const activeOutageWindows = getActiveOutageWindows(outageWindows);
      if (activeOutageWindows.length === 0) {
        return true;
      }

      // Get the maximum end date among all active outage windows
      const maxActiveOutageWindow = activeOutageWindows.reduce((max, current) => {
        const maxEndDate = dayjs(max.outage_end_date).startOf("day");
        const currentEndDate = dayjs(current.outage_end_date).startOf("day");
        return currentEndDate.isAfter(maxEndDate) ? current : max;
      });

      const outageEndDate = dayjs(maxActiveOutageWindow.outage_end_date).startOf("day");
      const plannedEndDate = formData.plannedEndDate ? dayjs(formData.plannedEndDate).startOf("day") : null;
      const actualEndDate = formData.actualEndDate ? dayjs(formData.actualEndDate).startOf("day") : null;
      if (plannedEndDate && plannedEndDate.isAfter(outageEndDate)) {
        setOutageWindowValidationError({
          open: true,
          outageWindowEndDate: formatDate(outageEndDate.toDate()),
          activeWindowsCount: activeOutageWindows.length,
        });
        return false;
      }

      if (actualEndDate && actualEndDate.isAfter(outageEndDate)) {
        setOutageWindowValidationError({
          open: true,
          outageWindowEndDate: formatDate(outageEndDate.toDate()),
          activeWindowsCount: activeOutageWindows.length,
        });
        return false;
      }

      return true;
    };

    const validateCommissioningPhaseCompletion = (): boolean => {
      if (!isCommissioningPhase || formData.status !== "Completed" || !outageWindows || outageWindows.length === 0) {
        return true;
      }

      const activeOutageWindows = getActiveOutageWindows(outageWindows);
      const actualEndDate = formData.actualEndDate ? dayjs(formData.actualEndDate).startOf("day") : null;

      // CASE 1: Active outage windows exist - validate actual end date
      if (activeOutageWindows.length > 0) {
        const maxActiveOutageWindow = activeOutageWindows.reduce((max, current) => {
          const maxEndDate = dayjs(max.outage_end_date).startOf("day");
          const currentEndDate = dayjs(current.outage_end_date).startOf("day");
          return currentEndDate.isAfter(maxEndDate) ? current : max;
        });

        const outageEndDate = dayjs(maxActiveOutageWindow.outage_end_date).startOf("day");

        if (actualEndDate && actualEndDate.isAfter(outageEndDate)) {
          if (isAllowedToOverrideCommissionPhaseCompletion) {
            // Allow admin/CAPDSLT with warning
            setCompletionValidationError({
              open: true,
              message: `Warning: The Actual End Date is after the active Outage Window End Date (${formatDate(outageEndDate.toDate())}). You can proceed, but it's recommended to update the Outage Window dates.`,
              canProceed: true,
            });
            return false;
          } else {
            // Block regular users
            setCompletionValidationError({
              open: true,
              message: `The Actual End Date must be equal to or before the active Outage Window End Date (${formatDate(outageEndDate.toDate())}). Please update the Outage Window dates and try again.`,
              canProceed: false,
            });
            return false;
          }
        }
        return true;
      }

      // CASE 2: No active windows - check if expired/inactive exist
      const expiredWindows = outageWindows.filter((w) => {
        const endDate = dayjs(w.outage_end_date).startOf("day");
        return endDate.isBefore(dayjs().startOf("day"));
      });

      const inactiveWindows = outageWindows.filter((w) => w.status === "in_active");

      if (expiredWindows.length > 0 || inactiveWindows.length > 0) {
        let message = "";
        if (expiredWindows.length > 0 && inactiveWindows.length > 0) {
          message = "The Outage Window(s) have expired and are inactive. The Commissioning Phase can only be completed during an active Outage Window period.";
        } else if (expiredWindows.length > 0) {
          message = "The Outage Window(s) have expired. The Commissioning Phase can only be completed during an active Outage Window period.";
        } else {
          message = "The Outage Window(s) are inactive. The Commissioning Phase can only be completed during an active Outage Window period.";
        }

        if (isAllowedToOverrideCommissionPhaseCompletion) {
          // Allow admin/CAPDSLT with warning
          setCompletionValidationError({
            open: true,
            message: `Warning: ${message} You can proceed anyway.`,
            canProceed: true,
          });
          return false;
        } else {
          // Block regular users
          setCompletionValidationError({
            open: true,
            message: message,
            canProceed: false,
          });
          return false;
        }
      }

      return true;
    };

    useEffect(() => {
      if (isEditing && initialData) {
        setFormData((prev) => ({
          ...prev,
          phaseId: initialData.phaseId ?? prev.phaseId,
          phase: initialData.phase ?? prev.phase,
          owner: initialData.owner ?? prev.owner,
          userId: initialData.userId ?? prev.userId,
          plannedStartDate: initialData.plannedStartDate ?? prev.plannedStartDate,
          plannedEndDate:
            initialData.plannedEndDate ?? prev.plannedEndDate,
          status: initialData.status ?? prev.status,
          color: initialData.color ?? prev.color,
          progress: initialData.progress ?? prev.progress,
          actualEndDate: initialData.actualEndDate ?? prev.actualEndDate,
        }));
      } else {
        setFormData(defaultFormData);
      }
    }, [initialData]);

    const phasesList = useMemo(() => {
      const list = phaseData?.data?.rows?.filter((r: any) => r.type === "phase") || [];
      return [...list].sort((a: any, b: any) => (a.sequence_order ?? 0) - (b.sequence_order ?? 0));
    }, [phaseData]);

    const adjacentPhases = useMemo(() => {
      let prev = null;
      let next = null;

      const currentIndex = phasesList.findIndex((p: any) => p.id === formData.phaseId);

      if (currentIndex !== -1) {
        if (currentIndex > 0) prev = phasesList[currentIndex - 1];
        if (currentIndex < phasesList.length - 1) next = phasesList[currentIndex + 1];
      } else if (phasesList.length > 0) {
        prev = phasesList[phasesList.length - 1];
      }

      return { prev, next };
    }, [phasesList, formData.phaseId]);

    const prevEndDate = adjacentPhases.prev?.planned_end_date ? dayjs(adjacentPhases.prev.planned_end_date).startOf('day') : null;
    const nextStartDate = adjacentPhases.next?.planned_start_date ? dayjs(adjacentPhases.next.planned_start_date).startOf('day') : null;

    const shouldDisablePlannedStartDate = (date: Dayjs) => {
      if (!isStageGateCompleted) return false;

      const today = dayjs().startOf("day");

      // allow currently selected value
      if (formData.plannedStartDate && date.isSame(dayjs(formData.plannedStartDate), "day")) {
        return false;
      }

      return date.isBefore(today);
    };

    const shouldDisablePlannedEndDate = (date: Dayjs) => {
      const today = dayjs().startOf("day");

      if (formData.plannedStartDate && date.isBefore(dayjs(formData.plannedStartDate).startOf("day"))) {
        return true;
      }

      if (!isStageGateCompleted) return false;

      if (formData.plannedEndDate &&
        date.isSame(dayjs(formData.plannedEndDate), "day")) {
        return false;
      }

      // For commissioning phases, constrain dates to active outage window
      if (isCommissioningPhase && outageWindows && outageWindows.length > 0) {
        const activeOutageWindows = getActiveOutageWindows(outageWindows);
        
        if (activeOutageWindows.length > 0) {
          const maxActiveOutageWindow = activeOutageWindows.reduce((max, current) => {
            const maxEndDate = dayjs(max.outage_end_date).startOf("day");
            const currentEndDate = dayjs(current.outage_end_date).startOf("day");
            return currentEndDate.isAfter(maxEndDate) ? current : max;
          });

          const maxOutageEndDate = dayjs(maxActiveOutageWindow.outage_end_date).startOf("day");
          if (date.isAfter(maxOutageEndDate)) {
            return true;
          }
        }
      }

      return date.isBefore(today);
    };

    const shouldDisableActualEndDate = (date: Dayjs) => {
      const today = dayjs().startOf("day");

      const plannedStart = formData.plannedStartDate
        ? dayjs(formData.plannedStartDate).startOf("day")
        : null;

      if (plannedStart && date.isBefore(plannedStart)) {
        return true;
      }

      if (date.isAfter(today)) {
        return true;
      }

      if (!isStageGateCompleted) return false;

      if (formData.actualEndDate && date.isSame(dayjs(formData.actualEndDate), "day")) { 
        return false; 
      }

      return false;
    };

    useImperativeHandle(ref, () => ({
      submit: () => {
        const newErrors: { phase?: string; plannedStartDate?: string; plannedEndDate?: string } = {};
        if (!formData.phase) {
          newErrors.phase = "Phase is required";
        }
        if (!formData.plannedStartDate) {
          newErrors.plannedStartDate = "Planned Start Date is required";
        }
        if (!formData.plannedEndDate) {
          newErrors.plannedEndDate = "Planned End Date is required";
        }
        if (Object.keys(newErrors).length > 0) {
          setErrors(newErrors);
          return;
        }

        // Validate outage windows first (blocks on failure)
        if (!validateCommissioningPhaseAgainstOutageWindows()) {
          return;
        }

        // Validate completion rules if marking as Completed
        if (formData.status === "Completed" && !validateCommissioningPhaseCompletion()) {
          return;
        }

        // Check for other warnings (adjacent phases, date conflicts)
        const warnings: string[] = [];

        if (formData.plannedStartDate && formData.plannedEndDate) {
          const start = dayjs(formData.plannedStartDate).startOf('day');
          const end = dayjs(formData.plannedEndDate).startOf('day');

          const isStartBeforePrevEnd = prevEndDate && start.isBefore(prevEndDate, 'day');
          const isStartAfterNextStart = nextStartDate && start.isAfter(nextStartDate, 'day');
          const isEndBeforePrevEnd = prevEndDate && end.isBefore(prevEndDate, 'day');
          const isEndAfterNextStart = nextStartDate && end.isAfter(nextStartDate, 'day');

          if (isStartBeforePrevEnd || isStartAfterNextStart || isEndBeforePrevEnd || isEndAfterNextStart) {
            warnings.push(
              "The selected planned dates overlap or fall out of sequence with the adjacent phases. Are you sure you want to proceed?"
            );
          }
        }

        if (formData.actualEndDate && formData.plannedStartDate) {
          const actualEnd = dayjs(formData.actualEndDate).startOf("day");
          const plannedStart = dayjs(formData.plannedStartDate).startOf("day");

          if (actualEnd.isBefore(plannedStart, "day")) {
            warnings.push(
              "The actual end date is earlier than the planned start date. Are you sure you want to proceed?"
            );
          }
        }

        if (warnings.length > 0) {
          setWarningQueue(warnings);
        } else {
          onSave(formData);
        }

      },
    }));

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
            flex: 1,
            gap: 3,
            p: 2,
          }}
        >

          {
            initialData && isStageGateCompleted && (
              <Alert severity="info">Cautious: The current stage gate for the phase has been approved.</Alert>
            )
          }

          <AutoComplete
            value={formData.phase}
            onSelect={(value) => {
              setFormData({ ...formData, phase: value });
              setErrors({ ...errors, phase: undefined });
            }}
            label="Phase"
            name={"phases"}
            isLabelRequired
            disabled={isEditing || phaseDisabledConfig.phase}
            error={!!errors.phase}
            helperText={errors.phase}
          />

          <FormControl fullWidth>
            <InputLabel>Owner</InputLabel>
            <Select
              value={formData.userId || ""}
              label="Owner"
              onChange={(e) => {
                const selectedUser = userOptions.find(u => u.id === e.target.value);
                setFormData({
                  ...formData,
                  userId: e.target.value as number,
                  owner: selectedUser?.name || ""
                });
              }}
              disabled={isLoadingUsers || phaseDisabledConfig.owner}
            >
              {isLoadingUsers ? (
                <MenuItem disabled>
                  <CircularProgress size={20} />
                </MenuItem>
              ) : userOptions.length > 0 ? (
                userOptions.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled value="">
                  No users available
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <DatePicker
            label="Planned Start Date"
            value={formData.plannedStartDate ? dayjs(formData.plannedStartDate) : null}
            onChange={(date) => {
              const newStartDate = date ? date.startOf("day").toISOString() : null;
              let newPlannedEndDate = formData.plannedEndDate;
              if (date && formData.plannedEndDate && date.isAfter(dayjs(formData.plannedEndDate).startOf("day"))) {
                newPlannedEndDate = newStartDate;
              }
              setFormData({
                ...formData,
                plannedStartDate: newStartDate,
                plannedEndDate: newPlannedEndDate,
              });
              setErrors({ ...errors, plannedStartDate: undefined });
            }}
            minDate={isStageGateCompleted ? dayjs().startOf('day') : undefined}
            shouldDisableDate={shouldDisablePlannedStartDate}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!errors.plannedStartDate,
                helperText: errors.plannedStartDate,
              }
            }}
            format="DD/MM/YYYY"
            disabled={phaseDisabledConfig.plannedStartDate}
          />

          <DatePicker
            label="Planned End Date"
            value={formData.plannedEndDate ? dayjs(formData.plannedEndDate) : null}
            onChange={(date) => {
              setFormData({
                ...formData,
                plannedEndDate: date ? date.startOf("day").toISOString() : null,
              });
              setErrors({ ...errors, plannedEndDate: undefined });
            }}
            minDate={formData.plannedStartDate ? dayjs(formData.plannedStartDate).startOf('day') : (isStageGateCompleted ? dayjs().startOf('day') : undefined)}
            shouldDisableDate={shouldDisablePlannedEndDate}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                error: !!errors.plannedEndDate,
                helperText: errors.plannedEndDate,
              }
            }}
            format="DD/MM/YYYY"
            disabled={phaseDisabledConfig.plannedEndDate}
          />

          <DatePicker
            label="Actual End Date"
            value={formData.actualEndDate ? dayjs(formData.actualEndDate) : null}
            onChange={(date) => {
              date && setFormData({
                ...formData,
                actualEndDate: date.startOf('day').toISOString()
              });
            }}
            maxDate={dayjs().startOf('day')}
            slotProps={{ textField: { fullWidth: true } }}
            format="DD/MM/YYYY"
            disabled={phaseDisabledConfig.actualEndDate || formData.status !== "Completed"}
          />

          {
            isEditing && areAllActivitiesCompleted === false && (
              <Alert severity="warning">
                Warning: Not all linked activities are completed. Please complete all activities to mark this phase as Completed.
              </Alert>
            )
          }

          {
            isEditing && hasLinkedStageGate && !isLinkedStageGateCompleted && (
              <Alert severity="error">
                Error: The linked stage gate must be marked as Completed before marking this phase as Completed.
              </Alert>
            )
          }

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              label="Status"
              onChange={(e) => {
                const newStatus = e.target.value;
                let newProgress = formData.progress;
                let newActualEndDate = formData.actualEndDate;
                if (newStatus === "Completed") {
                  newProgress = 100;
                  newActualEndDate = dayjs().startOf("day").toISOString();
                } else if (newStatus === "Not Started") {
                  newProgress = 0;
                  newActualEndDate = null;
                } else if (newStatus === "In Progress") {
                  newProgress = 5;
                  newActualEndDate = null;
                } else if (newStatus === "On Hold") {
                  const initialProgress = initialData?.progress || 0;
                  newActualEndDate = null;
                  if (initialProgress === 100) {
                    newProgress = 50;
                  } else if (initialProgress === 0) {
                    newProgress = 5;
                  } else {
                    newProgress = initialProgress;
                  }
                }
                setFormData({
                  ...formData,
                  status: newStatus,
                  progress: newProgress,
                  actualEndDate: newActualEndDate,
                });
              }}
              disabled={phaseDisabledConfig.status}
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem
                  key={option}
                  value={option}
                  disabled={
                    option === "Completed" && isEditing &&
                    (!areAllActivitiesCompleted || (hasLinkedStageGate && !isLinkedStageGateCompleted))
                  }
                >
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ px: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Percentage Completed
            </Typography>
            <Slider
              value={formData.progress}
              onChange={(_, value) => {
                const numValue = value as number;
                if (numValue === 100 && formData.status !== "Completed") {
                  return;
                }
                setFormData({ ...formData, progress: numValue })
              }}
              valueLabelDisplay="auto"
              step={5}
              marks
              min={formData.status === "In Progress" ? 5 : formData.status === "On Hold" ? 1 : 0}
              max={formData.status === "In Progress" ? 99 : 100}
              sx={{ mt: 2 }}
              disabled={phaseDisabledConfig.progress || formData.status === "Completed" || formData.status === "Not Started"}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
              <Typography variant="caption">0</Typography>
              <Typography variant="caption">25</Typography>
              <Typography variant="caption">50</Typography>
              <Typography variant="caption">70</Typography>
              <Typography variant="caption">100%</Typography>
            </Box>
          </Box>

          <Dialog open={!!currentWarning} onClose={handleWarningCancel}>
            <DialogTitle>Phase Date Warning</DialogTitle>
            <DialogContent>
              <DialogContentText>{currentWarning}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleWarningCancel}>Cancel</Button>
              <Button onClick={handleWarningIgnore} autoFocus>
                {warningQueue.length > 1 && warningQueue.indexOf(currentWarning!) &&
                  warningQueue.length - 1
                  ? "Ignore & Continue"
                  : "Ignore & Save"
                }
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={outageWindowValidationError.open} onClose={handleOutageWindowValidationClose}>
            <DialogTitle>Commissioning Phase Date Validation</DialogTitle>
            <DialogContent>
              {formData.status === "Completed" ? (
                <>
                  <Typography sx={{ mb: 2 }}>
                    There {outageWindowValidationError.activeWindowsCount === 1 ? "is an" : "are multiple"} active outage window{outageWindowValidationError.activeWindowsCount !== 1 ? "s" : ""} in this project. When completing the Commissioning Phase, the Actual End Date must fall within the Active Outage Window period.
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Active Outage Window End Date: <strong>{outageWindowValidationError.outageWindowEndDate}</strong>
                  </Typography>
                </>
              ) : (
                <>
                  <Typography sx={{ mb: 2 }}>
                    There {outageWindowValidationError.activeWindowsCount === 1 ? "is an" : "are multiple"} active outage window{outageWindowValidationError.activeWindowsCount !== 1 ? "s" : ""} in this project. The Commissioning Phase Planned End Date and Actual End Date must be equal to or before the Active Outage Window End Date.
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    Active Outage Window End Date: <strong>{outageWindowValidationError.outageWindowEndDate}</strong>
                  </Typography>
                </>
              )}
              <Typography>
                Please adjust the Commissioning Phase's dates and try again.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleOutageWindowValidationClose} variant="contained" color="primary">
                OK
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={completionValidationError.open} onClose={handleCompletionValidationClose}>
            <DialogTitle>Commissioning Phase Completion</DialogTitle>
            <DialogContent>
              <Typography sx={{ mb: 2 }}>
                {completionValidationError.message}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCompletionValidationClose} variant="outlined">
                Cancel
              </Button>
              {completionValidationError.canProceed && (
                <Button onClick={handleCompletionValidationProceed} variant="contained" color="primary">
                  Proceed
                </Button>
              )}
            </DialogActions>
          </Dialog>

        </Box>
      </LocalizationProvider>
    );
  }
);

export const PhaseFormActions: React.FC<{
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  isEditing?: boolean;
  isDeleteAllowed?: boolean;
  isEstimateValidationStageGateCompleted?: boolean;
  isCommercialFocusPhase?: boolean;
}> = ({ onSave, onCancel, onDelete, isEditing, isDeleteAllowed, isEstimateValidationStageGateCompleted, isCommercialFocusPhase }) => (
  <>
    <Button
      variant="outlined"
      onClick={onCancel}
      sx={{ textTransform: "uppercase" }}
    >
      Cancel
    </Button>
    {isEditing && onDelete && isDeleteAllowed && (!isEstimateValidationStageGateCompleted || !isCommercialFocusPhase) && (
      <Button
        variant="outlined"
        color="primary"
        onClick={onDelete}
        sx={{ textTransform: "uppercase" }}
      >
        Delete
      </Button>
    )}
    <Button
      variant="contained"
      onClick={onSave}
      sx={{ textTransform: "uppercase" }}
    >
      Save Details
    </Button>
  </>
);